import * as React from 'react';
import { Textarea } from '@/components/ui/textarea';
import { useAutoGrowTextarea } from '@hooks/useAutoGrowTextarea';

export interface AutoGrowTextareaProps extends React.ComponentProps<'textarea'> {
  /** Ceiling before the box scrolls instead of growing. */
  maxHeightPx?: number;
}

/**
 * Textarea that grows with its content, the way MUI's `multiline` TextField did.
 * `rows` still sets the floor. Plain `ui/textarea` clamps to a fixed height and
 * scrolls, which turns a pasted document into a keyhole.
 */
const AutoGrowTextarea = React.forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  ({ maxHeightPx = 400, value, defaultValue, onChange, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement>(null);
    React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTextAreaElement);

    // Uncontrolled fields have no value to depend on, so the resize is driven
    // from the change handler instead.
    const resize = useAutoGrowTextarea(innerRef, String(value ?? ''), maxHeightPx);

    return (
      <Textarea
        ref={innerRef}
        value={value}
        defaultValue={defaultValue}
        onChange={(event) => {
          onChange?.(event);
          resize();
        }}
        {...props}
      />
    );
  },
);
AutoGrowTextarea.displayName = 'AutoGrowTextarea';

export { AutoGrowTextarea };
